import { PropsWithChildren } from 'react';
import styles from './styles.module.scss';
import { ErrorCard } from '@starsoft/common/components';
import { useInfiniteScroll } from '@starsoft/common/hooks';
import AmountInput from '../../AmountInput';
import DepositMethods from './DepositMethods';
import { useFormContext } from 'react-hook-form';
import DepositCoinSelect from './CoinSelect';
import ValueSelector from './ValueSelector';
import { CoinType } from '@starsoft/common/models';
import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { TransactionsModalDepositContentFormLayoutProps } from './props';
import useDepositSearchParams from '../../../../../../../hooks/deposit/useDepositSearchParams';
import { useDepositForm } from '../../../../../../../hooks/deposit';
import TransactionsThirdPartyAlert from '../../../../../Common/ThirdPartyAlert';
import { Decimal } from '@starsoft/common/services/Decimal';

/**
 * TransactionsModalDepositContentFormLayout Component
 * This component is responsible for rendering the layout of the deposit form content.
 * It handles the display of various form elements such as coin selection, amount input,
 * value selector, and deposit methods. It also manages error handling and infinite scrolling.
 *
 * @param {PropsWithChildren<TransactionsModalDepositContentFormLayoutProps>} props - The properties for the component.
 * @param {JSX.Element} props.children - The child elements to be rendered within the layout.
 * @param {JSX.Element} props.additionalContent - Additional content to be rendered within the layout.
 *
 * @returns {JSX.Element} The rendered component displaying the deposit form layout.
 *
 * @example
 * // Usage example:
 * <TransactionsModalDepositContentFormLayout additionalContent={<div>Extra Content</div>}>
 *   <div>Child Content</div>
 * </TransactionsModalDepositContentFormLayout>
 */
export default function TransactionsModalDepositContentFormLayout({
  children,
  additionalContent,
}: PropsWithChildren<TransactionsModalDepositContentFormLayoutProps>) {
  // Retrieve the coin type from the deposit search parameters
  const { coinType } = useDepositSearchParams();
  // Use form context to watch and set form values
  const { watch, setValue, setFocus } =
    useFormContext<CreateDepositRequestDto>();
  // Watch the 'amount' field in the form
  const amount = watch('amount');
  // Use the deposit form hook to manage form state and queries
  const {
    formError,
    refetchForm,
    isFormLoading,
    depositMethodsQuery: {
      hasNextPage,
      isLoading: loadingDepositMethods,
      isFetchingNextPage,
      fetchNextPage,
    },
    accountByCoinQuery: { currentAccount },
    showExternalLink,
    coinMapping,
  } = useDepositForm(watch, setValue);

  // Use infinite scroll hook to manage loading more data
  const { ref } = useInfiniteScroll({
    hasMore: hasNextPage,
    isLoading: loadingDepositMethods || isFetchingNextPage,
    loadMore: fetchNextPage,
  });

  /**
   * onValueChange Function
   * This function updates the 'amount' field in the form when the value changes.
   *
   * @param {Decimal} value - The new value to set for the 'amount' field.
   */
  function onValueChange(value: Decimal) {
    setValue('amount', value.toNumber(), {
      shouldDirty: true,
      shouldTouch: true,
    });
    setFocus('amount');
  }

  // Render an error card if there is a form error
  if (formError) {
    return (
      <div className={styles.container}>
        <div className={styles.container__content} ref={ref}>
          <ErrorCard error={formError} refetch={refetchForm} />
        </div>
      </div>
    );
  }

  // Render the deposit form layout
  return (
    <div className={styles.container}>
      <div
        className={`${styles.container__content} ${
          coinType === CoinType.Crypto
            ? styles['container__content--crypto']
            : ''
        }`}
        ref={ref}
      >
        <DepositCoinSelect hideInput={coinType == CoinType.Crypto} />
        <AmountInput />
        <ValueSelector
          coin={currentAccount?.coin}
          onSelectValue={onValueChange}
          currentValue={new Decimal(amount)}
          isLoading={isFormLoading}
          suggestedAmounts={coinMapping?.suggestedAmounts}
        />
        <DepositMethods />
        {children}
        <TransactionsThirdPartyAlert hidden={!showExternalLink} />
        {additionalContent}
      </div>
    </div>
  );
}
